import axios, { AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import Keycloak from 'keycloak-js'
import { ROUTES } from '@shared/constants/routes'
import { getAuthCallbackUrl } from '@shared/lib/authRedirect'
import { URLS } from '@assets/environment/urls'
import { authTransport } from './auth-transport'
import type { EnsureFreshSession } from './auth-transport.types'

export { authorizedFetch } from './auth-transport'
export type { EnsureFreshSession, EnsureFreshSessionOptions } from './auth-transport.types'

// this thing is used for requests with auth header
export let authAxios: AxiosInstance = axios.create()

interface AuthAxiosRequestConfig extends InternalAxiosRequestConfig {
	auth401Retried?: boolean
}

const getClientTimezone = (): string | null => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || null
	} catch {
		return null
	}
}

const getRequestOrigin = (config: InternalAxiosRequestConfig): string | null => {
	try {
		return new URL(config.url ?? '', config.baseURL ?? 'http://localhost').origin
	} catch {
		return null
	}
}

const isKeycloakRequest = (config: InternalAxiosRequestConfig): boolean => {
	if (!URLS.KC_URL) return false

	try {
		return getRequestOrigin(config) === new URL(URLS.KC_URL).origin
	} catch {
		return false
	}
}

class ApiService {
	constructor() {
		this.resetAxios()
	}

	public setup(keycloak: Keycloak): void {
		if (!keycloak.token) {
			console.error('Токен Keycloak отсутствует.')
			authTransport.reset()
			return
		}

		authTransport.configure(async options => {
			const hasNewerAccessToken = Boolean(
				options?.rejectedAccessToken &&
					keycloak.token &&
					keycloak.token !== options.rejectedAccessToken
			)
			if (!hasNewerAccessToken && (options?.forceRefresh || keycloak.isTokenExpired(30))) {
				try {
					await keycloak.updateToken(options?.forceRefresh ? -1 : 30)
				} catch {
					console.error('Не удалось обновить токен')
					void keycloak.logout({
						redirectUri: getAuthCallbackUrl(ROUTES.overall.hello),
					})
					return null
				}
			}
			return keycloak.token ?? null
		})
	}

	public setupToken(accessToken: string, accessTokenProvider: EnsureFreshSession): void {
		if (!accessToken) {
			console.error('Access token отсутствует.')
			authTransport.reset()
			return
		}

		authTransport.configure(accessTokenProvider)
	}

	public clearToken(): void {
		authTransport.reset()
	}

	public resetForTests(): void {
		this.clearToken()
		this.resetAxios()
	}

	private resetAxios(): void {
		authAxios = axios.create()
		authAxios.interceptors.request.use(async config => {
			const accessToken = await authTransport.requireAccessToken()
			if (!accessToken) {
				this.redactAuthorization(config.headers)
				throw new Error('Active auth session is missing')
			}

			config.headers.set('Authorization', `Bearer ${accessToken}`)
			const timezone = isKeycloakRequest(config) ? null : getClientTimezone()
			if (timezone) {
				config.headers.set('X-Timezone', timezone)
			}
			return config
		})

		authAxios.interceptors.response.use(
			response => response,
			async error => {
				if (!axios.isAxiosError(error) || error.response?.status !== 401) {
					this.redactAxiosError(error)
					throw error
				}

				const originalConfig = error.config as AuthAxiosRequestConfig | undefined
				if (!originalConfig || originalConfig.auth401Retried) {
					this.redactAxiosError(error)
					throw error
				}

				let retryAccessToken: string | null
				try {
					retryAccessToken = await authTransport.getAccessToken({
						forceRefresh: true,
						rejectedAccessToken: this.getBearerAccessToken(originalConfig) ?? undefined,
					})
				} catch {
					this.redactAxiosError(error)
					throw error
				}

				if (!retryAccessToken) {
					this.redactAxiosError(error)
					throw error
				}
				if (!this.isSafeReplayMethod(originalConfig.method)) {
					this.redactAxiosError(error)
					throw error
				}

				originalConfig.auth401Retried = true
				originalConfig.headers.set('Authorization', `Bearer ${retryAccessToken}`)
				return authAxios.request(originalConfig)
			}
		)
	}

	private isSafeReplayMethod(method: string | undefined): boolean {
		return ['get', 'head', 'options'].includes((method ?? 'get').toLowerCase())
	}

	private getBearerAccessToken(config: InternalAxiosRequestConfig): string | null {
		const authorization = config.headers.get('Authorization')
		if (typeof authorization !== 'string') return null
		const match = authorization.match(/^Bearer\s+(.+)$/i)
		return match?.[1] ?? null
	}

	private redactAxiosError(error: unknown): void {
		if (!axios.isAxiosError(error)) return
		this.redactAuthorization(error.config?.headers)
		this.redactAuthorization(error.response?.config.headers)
	}

	private redactAuthorization(headers: unknown): void {
		if (!headers || typeof headers !== 'object') return
		const candidate = headers as {
			delete?: (name: string) => void
			[key: string]: unknown
		}
		if (typeof candidate.delete === 'function') {
			candidate.delete('Authorization')
			candidate.delete('authorization')
			return
		}
		for (const key of Object.keys(candidate)) {
			if (key.toLowerCase() === 'authorization') delete candidate[key]
		}
	}
}

// Yes, it's costyl, but it's only used in auth providers and hard to change to normal
export const apiService = new ApiService()
